import {
  GLOBAL_PERIOD_LABELS,
  GLOBAL_PERIOD_VALUES,
  GLOBAL_RANGE_LABELS,
  GLOBAL_RANGE_VALUES,
  useGlobalFilters,
  type GlobalPeriod,
  type GlobalRange,
} from "../lib/globalFilters";
import { isCompleteMonth, monthLabel, monthSelectOptions } from "../lib/calendarMonth.mjs";
import { useExecKpiAvailability, useExecKpiLatestPeriod } from "../lib/execKpiData";
import { FILTER_LEGEND, periodLegend, rangeLegend } from "../lib/filterLegend.mjs";
import { humanize } from "../lib/labels";

/**
 * Top-of-tab control strip. Five groups:
 *
 * - Period: Monthly / Weekly / Daily.
 * - Range: MTD / WTD / L30 / L90 / YTD.
 * - Month (MER-692): a native select of recent complete calendar months, plus
 *   a "Workbook month" chip when the tenant has an executive workbook.
 * - Division: multi-select pills sourced from manifest.divisions (MER-163).
 * - LOB: multi-select pills sourced from manifest.enabled_services (MER-163).
 *
 * Selection persists to URL search params so the view is shareable.
 */

export interface DashboardFilterBarProps {
  /** MER-163: divisions for the Division multi-select. From manifest. */
  divisions?: string[];
  /** MER-163: services for the LOB multi-select. From manifest. */
  services?: string[];
}

// MER-179: division labels here are Meridian's customer-facing names
// for the operating cities. They are not overrides for snake_case
// humanization (`jackson` -> `Jackson` already works through humanize)
// but kept explicit so a future customer with multi-word city names
// (e.g. "new_orleans" -> "New Orleans") stays correct without relying
// on the helper. Empty fall-throughs route to humanize.
const DIVISION_LABELS: Record<string, string> = {
  jackson: "Jackson",
  hattiesburg: "Hattiesburg",
  dallas: "Dallas",
};

// MER-179: SERVICE_LABELS keeps the bar's chosen display strings.
// "Residential" is the bar's preferred label for `asl`, since the
// filter slot is wider than the canonical "Side Load" synonym. The
// shared humanize helper renders `asl` -> "Side Load" elsewhere.
const SERVICE_LABELS: Record<string, string> = {
  front_load: "Front Load",
  roll_off: "Roll Off",
  asl: "Residential",
};

interface SegmentProps<T extends string> {
  label: string;
  values: readonly T[];
  labels: Record<T, string>;
  active: T | undefined;
  onChange: (v: T) => void;
  // MER-421 Root 2: optional leading "neutral" pill (e.g. "Default") that means
  // no override. Active when `neutral.active`; selecting it calls
  // `neutral.onSelect` instead of onChange, and no value pill is highlighted.
  neutral?: { label: string; active: boolean; onSelect: () => void };
}

function Segment<T extends string>(props: SegmentProps<T>) {
  const { label, values, labels, active, onChange, neutral } = props;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: "inline-flex",
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 999,
          padding: 2,
          gap: 2,
        }}
      >
        {neutral ? (
          <button
            type="button"
            role="radio"
            aria-checked={neutral.active}
            onClick={neutral.onSelect}
            style={{
              appearance: "none",
              border: "none",
              background: neutral.active ? "var(--brand-deep)" : "transparent",
              color: neutral.active ? "#ffffff" : "var(--text-primary)",
              fontSize: 12,
              fontWeight: 600,
              padding: "5px 12px",
              borderRadius: 999,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {neutral.label}
          </button>
        ) : null}
        {values.map((v) => {
          const isActive = v === active;
          return (
            <button
              key={v}
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange(v)}
              style={{
                appearance: "none",
                border: "none",
                background: isActive ? "var(--brand-deep)" : "transparent",
                color: isActive ? "#ffffff" : "var(--text-primary)",
                fontSize: 12,
                fontWeight: 600,
                padding: "5px 12px",
                borderRadius: 999,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {labels[v]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface MultiSegmentProps {
  label: string;
  values: readonly string[];
  labels: Record<string, string>;
  active: string[];
  onChange: (next: string[]) => void;
}

function MultiSegment(props: MultiSegmentProps) {
  const { label, values, labels, active, onChange } = props;
  const activeSet = new Set(active);
  const allActive = active.length === 0;
  const toggle = (v: string) => {
    if (activeSet.has(v)) {
      const next = active.filter((x) => x !== v);
      // If clearing the last selection, return to "all" (empty array).
      onChange(next);
    } else {
      const next = [...active, v];
      // MER-504 follow-up: selecting EVERY value is mathematically the
      // unfiltered set, so collapse to the explicit All state instead of
      // showing a fully-lit pill row that looks filtered but is not (the
      // mirror image of the AR company-wide lie of context).
      onChange(next.length === values.length ? [] : next);
    }
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <div
        role="group"
        aria-label={label}
        style={{
          display: "inline-flex",
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 999,
          padding: 2,
          gap: 2,
        }}
      >
        <button
          type="button"
          aria-pressed={allActive}
          onClick={() => onChange([])}
          style={{
            appearance: "none",
            border: "none",
            background: allActive ? "var(--brand-deep)" : "transparent",
            color: allActive ? "#ffffff" : "var(--text-primary)",
            fontSize: 12,
            fontWeight: 600,
            padding: "5px 12px",
            borderRadius: 999,
            cursor: "pointer",
          }}
        >
          All
        </button>
        {values.map((v) => {
          const isActive = activeSet.has(v);
          return (
            <button
              key={v}
              type="button"
              aria-pressed={isActive}
              onClick={() => toggle(v)}
              style={{
                appearance: "none",
                border: "none",
                background: isActive ? "var(--brand-deep)" : "transparent",
                color: isActive ? "#ffffff" : "var(--text-primary)",
                fontSize: 12,
                fontWeight: 600,
                padding: "5px 12px",
                borderRadius: 999,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {labels[v] ?? humanize(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** MER-692: how many complete calendar months the Month select offers. */
const MONTH_OPTION_COUNT = 18;

/** MER-692: stable id binding the visible "Month" label to its select. */
const MONTH_SELECT_ID = "dashboard-filter-month";

interface MonthControlProps {
  month: string | null;
  onMonth: (month: string) => void;
}

/**
 * MER-692: pick one calendar month, so any tab can show the same month as a
 * client's month-end workbook.
 *
 * The month in progress and future months are never offered: a partial month
 * read against a complete month-end figure is the partial-vs-full trap. A
 * month already in the URL that is not on the list (older than it, or a
 * current or future month typed into a link) is added as the selected option,
 * marked "(in progress)" or "(future)" where it applies, so the control always
 * names the window the page is showing (monthSelectOptions).
 *
 * The "Workbook month" chip selects the workbook's month and nothing else.
 * With Period on Default a month already rolls every tile it overrides up to
 * monthly grain (grainDefaults.mjs), so the chip needs no explicit Monthly,
 * and Range Default afterwards returns every tile to its declared default.
 * It is active whenever the chosen month is the workbook's month.
 */
function MonthControl(props: MonthControlProps) {
  const { month, onMonth } = props;
  const today = new Date();
  const options = monthSelectOptions(today, MONTH_OPTION_COUNT, month);
  const selectValue = month !== null && options.some((o) => o.value === month) ? month : "";

  const execAvailable = useExecKpiAvailability() === "available";
  const workbookLatest = useExecKpiLatestPeriod();
  const workbookMonth =
    execAvailable && workbookLatest !== null && isCompleteMonth(workbookLatest, today)
      ? workbookLatest
      : null;
  const workbookActive = workbookMonth !== null && month === workbookMonth;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <label
        htmlFor={MONTH_SELECT_ID}
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-muted)",
        }}
      >
        Month
      </label>
      <select
        id={MONTH_SELECT_ID}
        value={selectValue}
        onChange={(event) => {
          if (event.target.value) onMonth(event.target.value);
        }}
        style={{
          background: "var(--bg-card)",
          border: `1px solid ${month !== null ? "var(--brand-deep)" : "var(--border-subtle)"}`,
          boxShadow: month !== null ? "inset 0 0 0 1px var(--brand-deep)" : "none",
          borderRadius: 999,
          color: "var(--text-primary)",
          fontSize: 12,
          fontWeight: 600,
          padding: "5px 10px",
          cursor: "pointer",
        }}
      >
        <option value="" disabled>
          Choose month
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {workbookMonth !== null ? (
        <div
          style={{
            display: "inline-flex",
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 999,
            padding: 2,
          }}
        >
          <button
            type="button"
            aria-pressed={workbookActive}
            onClick={() => onMonth(workbookMonth)}
            title="Show the latest month in the executive workbook"
            style={{
              appearance: "none",
              border: "none",
              background: workbookActive ? "var(--brand-deep)" : "transparent",
              color: workbookActive ? "var(--panel)" : "var(--text-primary)",
              fontSize: 12,
              fontWeight: 600,
              padding: "5px 12px",
              borderRadius: 999,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Workbook month: {monthLabel(workbookMonth)}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function DashboardFilterBar(props: DashboardFilterBarProps = {}) {
  const { divisions: divisionOptions = [], services: serviceOptions = [] } = props;
  const {
    period,
    periodExplicit,
    range,
    rangeExplicit,
    month,
    divisions,
    services,
    setPeriod,
    clearPeriod,
    setRange,
    clearRange,
    setMonth,
    setDivisions,
    setServices,
  } = useGlobalFilters();
  // Period, Range and Month are always present; Division and LOB only when the
  // manifest gives this customer more than one to choose from.
  const controlCount =
    3 + (divisionOptions.length > 0 ? 1 : 0) + (serviceOptions.length > 0 ? 1 : 0);
  return (
    <div
      style={{
        marginBottom: 20,
        paddingBottom: 16,
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          // MER-584: tighter than the old 24 so the four groups fit one row at
          // common widths. It still wraps below about 1500px of CONTENT, and it
          // should: the mockup was drawn without a sidebar, and the sidebar takes
          // 240px. Wrapping is the correct answer at that point -- squeezing four
          // pill groups onto one line is how the tiles ended up clipping.
          gap: 16,
          alignItems: "center",
        }}
      >
        {/* MER-497: mirror the Range segment's MER-421 shape. The Default chip
          means "each tile's declared grain"; a period chip is only active on
          an EXPLICIT selection, so the bar never claims Monthly while
          weekly-declared tiles render weekly data. */}
        <Segment<GlobalPeriod>
          label="Period"
          values={GLOBAL_PERIOD_VALUES}
          labels={GLOBAL_PERIOD_LABELS}
          active={periodExplicit ? period : undefined}
          onChange={setPeriod}
          neutral={{ label: "Default", active: !periodExplicit, onSelect: clearPeriod }}
        />
        {/* MER-692: a selected month lights no preset chip, and Default is only
          active when neither a preset nor a month is set; Default clears both. */}
        <Segment<GlobalRange>
          label="Range"
          values={GLOBAL_RANGE_VALUES}
          labels={GLOBAL_RANGE_LABELS}
          active={rangeExplicit ? range : undefined}
          onChange={setRange}
          neutral={{
            label: "Default",
            active: !rangeExplicit && month === null,
            onSelect: clearRange,
          }}
        />
        <MonthControl month={month} onMonth={setMonth} />
        {divisionOptions.length > 0 ? (
          <MultiSegment
            label="Division"
            values={divisionOptions}
            labels={DIVISION_LABELS}
            active={divisions}
            onChange={setDivisions}
          />
        ) : null}
        {serviceOptions.length > 0 ? (
          <MultiSegment
            label="LOB"
            values={serviceOptions}
            labels={SERVICE_LABELS}
            active={services}
            onChange={setServices}
          />
        ) : null}
        {/* MER-584: the mockup's right-aligned control note. Counting the controls
          is not the point -- "scoped to this tab's sources" is. A reader who
          selects Jackson and sees a tile unchanged needs to know the tile's
          source has no division grain, rather than assume the filter is
          broken. The per-tile "Company-wide" face note says it tile by tile;
          this says it once for the bar. */}
        <div
          style={{
            marginLeft: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            color: "var(--text-muted)",
            whiteSpace: "nowrap",
          }}
        >
          {controlCount} {controlCount === 1 ? "control" : "controls"} · scoped to this tab&apos;s
          sources
        </div>
      </div>
      {/* MER-612 + MER-611: what the two words mean, and what the page is doing
          with them right now. Zay asked the difference between period and range
          twice on the 2026-09-02 call and asked where the default is stated.
          The Default chip alone cannot answer either: it is the absence of an
          override, and an absence looks the same whether it means "year to
          date" (what the meeting notes recorded, wrongly) or "each tile keeps
          what it was built with" (what it actually does). */}
      <div
        style={{
          marginTop: 10,
          display: "flex",
          flexWrap: "wrap",
          gap: "2px 12px",
          fontSize: 11,
          lineHeight: 1.5,
          color: "var(--text-muted)",
        }}
      >
        <span>{FILTER_LEGEND}</span>
        <span>
          {periodLegend({ explicit: periodExplicit, label: GLOBAL_PERIOD_LABELS[period] })}
        </span>
        <span>
          {rangeLegend({
            explicit: rangeExplicit,
            label: GLOBAL_RANGE_LABELS[range],
            month: month === null ? null : monthLabel(month),
          })}
        </span>
      </div>
    </div>
  );
}
